import React from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { InputAdornment, TextField } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useDebouncedCallback } from 'use-debounce';
import useFundsStateStore from '../../common/funds-state-hook';
import { useIsLarge } from '../../../../shared/theme';

const useStyles = makeStyles(() => ({
  root: {
    minWidth: 240,
    margin: props => (props.isLarge ? '0 12px 0 auto' : '0 0 30px 0'),

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },

    '& .MuiOutlinedInput-root': {
      height: props => (props.isLarge ? 36 : 40),
      border: '1px solid #8EA3B5',

      '&:hover': {
        border: '1px solid #627484',
      },
    },

    '& .MuiOutlinedInput-inputAdornedStart': {
      paddingLeft: 4,
    },

    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: 12,
    },

    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: 12,
    },

    '& .MuiInputAdornment-root': {
      color: '#8EA3B5',
    },

    '& .MuiInputAdornment-positionStart > .icon-lw': {
      fontSize: 18,
      fontWeight: 600,
    },

    '& .MuiInputAdornment-positionEnd': {
      height: 20,
      width: 20,
      marginRight: -4,

      '& .MuiCircularProgress-root': {
        height: '16px !important',
        width: '16px !important',

        '& > svg': {
          color: '#19202d',
        },
      },

      '&.clear-icon:hover': {
        cursor: 'pointer',

        '& > svg': {
          color: '#19202d',
        },
      },

      '& > svg': {
        fontSize: 16,
      },
    },

    '& input': {
      fontFamily: 'Texta,Arial,Helvetica,sans-serif',
      fontSize: 16,
      lineHeight: '120%',
      color: '#19202d',
    },
  },
}));

const buildEndAdornment = (value, loading, onClick) => {
  if (loading) {
    return (
      <InputAdornment position="end">
        <CircularProgress color="inherit" />
      </InputAdornment>
    );
  }

  return !value ? null : (
    <InputAdornment className="clear-icon" position="end" onClick={onClick}>
      <ClearIcon />
    </InputAdornment>
  );
};

export default function NameFilter(props) {
  const notFirstLoad = useFundsStateStore(state => state.notFirstLoad);
  const { qname } = props;
  const [value, setValue] = React.useState();
  const classes = useStyles({ isLarge: useIsLarge() });
  const set = useFundsStateStore(state => state.set);
  const loadingFunds = useFundsStateStore(state => state.loadingFunds);

  if (qname && !notFirstLoad) {
    set(state => {
      state.filters.name = qname;
      state.filterChanged = true;
      state.notFirstLoad = true;
    });
    setValue(qname);
  }

  const { callback: debounced } = useDebouncedCallback(val => {
    if (val === '' || val.length >= 3) {
      set(state => {
        state.filters.name = val;
        state.filterChanged = true;
      });
    }
  }, 100);

  const onChange = e => {
    const val = e.target.value;
    setValue(val);

    debounced(val.trim());
  };

  const reset = React.useCallback(() => {
    setValue('');

    set(state => {
      state.filters.name = '';
      state.filterChanged = true;
    });
  }, [set]);

  return (
    <TextField
      className={classes.root}
      variant="outlined"
      placeholder="Search Fund Name"
      value={value}
      onChange={onChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <i className="icon-lw icon-lw-magnifying-glass" />
          </InputAdornment>
        ),
        endAdornment: buildEndAdornment(value, loadingFunds, reset),
      }}
    />
  );
}

NameFilter.propTypes = {
  /* eslint-disable-next-line react/require-default-props */
  qname: PropTypes.string,
};
